import * as React from 'react';
import { createContext } from 'react';

import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import HasUserEnforcedSSOLoginQuery from 'bundles/account-settings/queries/HasUserEnforcedSSOLogin.graphql';
import type {
  HasUserEnforcedSsoLoginQuery as HasUserEnforcedSsoLoginQueryType,
  HasUserEnforcedSsoLoginQueryVariables,
} from 'bundles/account-settings/queries/__generated__/HasUserEnforcedSSOLogin';
import useForcedSsoEpicEnabled from 'bundles/saml-sso/shared/hooks/useForcedSsoEpicEnabled';

export type UserForcedSSOContextType = {
  isUserSsoLoginForced: boolean;
};

type UserForcedSSOContextConsumerProps = {
  children: (context: UserForcedSSOContextType) => React.ReactElement;
};

export const UserForcedSSOContext = createContext<UserForcedSSOContextType | undefined>(undefined);

export const UserForcedSSOContextProvider: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  const { id: userId, email_address: emailAddress } = user.get();
  const { isEmailAllocatedForForceSsoEpic, loading: userEpicForceSsoLoading } = useForcedSsoEpicEnabled(emailAddress);

  const { data: userForcedSsoData, loading: userForceSsoLoading } = useQuery<
    HasUserEnforcedSsoLoginQueryType,
    HasUserEnforcedSsoLoginQueryVariables
  >(HasUserEnforcedSSOLoginQuery, {
    skip: !isEmailAllocatedForForceSsoEpic,
    variables: { userId: userId.toString() },
    context: { clientName: 'gatewayGql' },
  });
  const isUserSsoLoginForced =
    !!userForcedSsoData?.EntSso?.queryIfUserNeedSsoLoginByUserIdV2?.isSsoLoginForced && isEmailAllocatedForForceSsoEpic;
  if (userEpicForceSsoLoading || userForceSsoLoading) {
    return null;
  }

  return <UserForcedSSOContext.Provider value={{ isUserSsoLoginForced }}>{children}</UserForcedSSOContext.Provider>;
};

export const useUserForcedSSOContext = () => {
  const context = React.useContext(UserForcedSSOContext);

  if (!context) {
    throw new Error('useUserForcedSSOContext must be used within a UserForcedSSOContextProvider');
  }

  return context;
};

export const UserForcedSSOContextConsumer = ({ children }: UserForcedSSOContextConsumerProps) => {
  const context = useUserForcedSSOContext();
  return children(context);
};
